import { VISIBILITY_STATUS } from '../constants/visibilityStatus';
import { AVAILABILITY_STATUS } from '../constants/availabilityStatus';

const { VISIBLE, HIDDEN } = VISIBILITY_STATUS;
const { AVAILABLE, RESERVED, SOLD, UNKNOWN } = AVAILABILITY_STATUS;

const isValueInSet = (value, set) => {
	if (!Array.isArray(set)) return false;
	return set.some((item) => String(item) === String(value));
};

/**
 * Safely retrieves a value from a nested object based on the provided path.
 * @param {object} obj - The source object.
 * @param {string[]} path - An array of strings representing the path to the value.
 * @param {*} [defaultValue=undefined] - The value returned if the path does not exist or is invalid.
 * @returns {*} The value found at the path or the defaultValue.
 */
const getKeyValueFromInObjectPath = (obj, path, defaultValue = undefined) => {
	if (!obj || !path || !Array.isArray(path)) {
		return defaultValue;
	}
	let current = obj;
	for (let i = 0; i < path.length; i++) {
		const key = path[i];
		if (
			current &&
			typeof current === 'object' &&
			Object.prototype.hasOwnProperty.call(current, key)
		) {
			current = current[key];
		} else {
			return defaultValue;
		}
	}
	return current;
};

/**
 * Calculates the visibility status of an apartment based on CRM data and configuration.
 * @param {object} crmUnit - The CRM product object.
 * @param {object} visibilityConfig - Visibility configuration from the CRM dictionary.
 * @returns {string} The visibility status (e.g., VISIBLE, HIDDEN).
 */
const getCrmUnitVisibility = (crmUnit, visibilityConfig) => {
	if (!crmUnit) {
		return HIDDEN;
	}
	if (
		!visibilityConfig ||
		!visibilityConfig.path ||
		visibilityConfig.hidden_values === undefined
	) {
		// Since crmUnit is truthy, even though visibilityConfig is not defined or incomplete we can assume that the product is visible in CRM
		return VISIBLE;
	}

	const crmVisibilityValue = getKeyValueFromInObjectPath(
		crmUnit,
		visibilityConfig.path,
		null
	);
	if (isValueInSet(crmVisibilityValue, visibilityConfig.hidden_values)) {
		return HIDDEN; // W CRM oznaczony jako niewidoczny
	}
	return VISIBLE; // W CRM oznaczony jako widoczny
};

/**
 * Calculates the availability status of an apartment based on CRM data and configuration.
 * @param {object} crmUnit - The CRM product object.
 * @param {object} availabilityConfig - Availability configuration from the CRM dictionary.
 * @returns {string} The availability status (e.g., AVAILABLE, SOLD, RESERVED, UNKNOWN).
 */
const getCrmUnitAvailability = (crmUnit, availabilityConfig) => {
	if (!crmUnit) {
		return UNKNOWN;
	}
	if (
		!availabilityConfig ||
		!availabilityConfig.path ||
		!availabilityConfig.available_values ||
		!availabilityConfig.reserved_values ||
		!availabilityConfig.sold_values
	) {
		console.warn(
			'Context: Brak lub niekompletna konfiguracja dostępności w słowniku CRM. Ustawiam UNKNOWN.'
		);
		return UNKNOWN;
	}

	const crmAvailabilityValue = getKeyValueFromInObjectPath(
		crmUnit,
		availabilityConfig.path,
		null
	);

	if (isValueInSet(crmAvailabilityValue, availabilityConfig.available_values)) {
		return AVAILABLE;
	}
	if (isValueInSet(crmAvailabilityValue, availabilityConfig.reserved_values)) {
		return RESERVED;
	}
	if (isValueInSet(crmAvailabilityValue, availabilityConfig.sold_values)) {
		return SOLD;
	}
	console.warn(
		`calculateApartmentCrmAvailability: Nieznana wartość statusu dostępności z CRM: "${crmAvailabilityValue}". Ustawiam "${UNKNOWN}".`
	);
	return UNKNOWN;
};
/**
 * Determines the visibility of the price in the CRM unit.
 * @param {*} crmUnit - The CRM unit object.
 * @param {*} priceVisibilityConfig - The price visibility configuration.
 * @returns {boolean} - True if the price is visible, false otherwise.
 */
const getCrmUnitPriceVisibility = (crmUnit, priceVisibilityConfig) => {
	if (!crmUnit) {
		return false;
	}

	const visibilityValue = getKeyValueFromInObjectPath(
		crmUnit,
		priceVisibilityConfig.path,
		null
	);
	return isValueInSet(visibilityValue, priceVisibilityConfig.visible_values);
};

const normalizeData = (data) => {
	return data.map((sourceApartment) => {
		const apartment = { ...sourceApartment };
		const apartmentId = apartment.id_cad;

		apartment.visibility = VISIBLE;
		apartment.availability = apartment.availability ?? AVAILABLE;
		apartment.price_visible = apartment.price_visible ?? true;
		apartment.price_brutto = apartment.price_brutto ?? null;
		apartment.price_m2 = apartment.price_m2 ?? null;
		apartment.price_history = apartment.price_history ?? [];
		apartment.price_offer = apartment.price_offer ?? {
			on_offer: false,
			date: null,
		};
		apartment.price_vat = apartment.price_vat ?? null;
		apartment.price_currency = apartment.price_currency ?? 'PLN';
		apartment.flags = apartment.flags ?? {
			turnkey: false,
			smart_home: false,
			eco: false,
			finished: false,
			done_soon: false,
			last_units: false,
			with_parking: false,
			with_storage: false,
			accessible: false,
		};
		apartment.tier_id = apartmentId?.split('-')[2] ?? null; //TODO: if i can get this form id_cad than do i need it as property in the data source?
		apartment.building_id = apartmentId?.split('-')[0] ?? null;  //TODO: if i can get this form id_cad than do i need it as property in the data source?
		//TODO: temoporary keys to remain compatible with old version of the app
		apartment.edifice_name =
			apartmentId?.split('-')[0].replace(/^0+/, '') //THIS SHOULD COME FROM BUILDINGS DATA!!! TODO - important!!!
			// apartmentId?.split('-')[0] === '0D'
			// 	? 'D'
			// 	: apartmentId?.split('-')[0] === '0C'
			// 	? 'C'
			// 	: apartmentId?.split('-')[0] === '0B'
			// 	? 'B'
			// 	: apartmentId?.split('-')[0] === '0A'
			// 	? 'A'
			// 	: apartmentId?.split('-')[0];
		apartment.ref_tier = parseInt(apartmentId?.split('-')[2]) ?? null;
		return apartment;
	});
};

const normalizeBase = (data) => {
	return normalizeData(data);
};

const normalizeCms = (data) => {
	// CMS data will not come as an array of apartments, but rather as a object with building id_cad as keys nad two properties: 'original_name' (string) and 'apartments' (array)
	const apartments = [];
	if (!data) {
		return apartments;
	}

	for (const [
		buildingId,
		{ original_name, apartments: cmsApartments },
	] of Object.entries(data)) {
		for (const cmsApartment of cmsApartments) {
			// if (cmsApartment.visibility === HIDDEN) {
			// 	continue;
			// }
			const exists = apartments.some(
				(newApartment) => newApartment.id_cad === cmsApartment.id_cad
			);
			if (exists) {
				continue;
			}
			apartments.push({
				...cmsApartment,
				building_id: buildingId,
			});
		}
	}

	return normalizeData(apartments);
};

const normalizeCrm = (data, dictionary) => {
	const apartments = [];
	if (!data) {
		return apartments;
	}
	for (const crmUnit of data) {
		//1. Exclude apartments
		//1.1. Omit duplicates based on crm id
		const crmUnitId = getKeyValueFromInObjectPath(
			crmUnit,
			dictionary.unit_id_path,
			null
		);
		const exists = apartments.some(
			(newApartment) => newApartment.crm_unit_id === crmUnitId
		);
		if (exists) {
			continue;
		}
		// //1.2. Omit hidden apartments
		// const crmUnitVisibility = getCrmUnitVisibility(
		// 	crmUnit,
		// 	dictionary.visibility_config
		// );
		// if (crmUnitVisibility === HIDDEN) {
		// 	continue;
		// }

		// Map CRM data to our apartment structure
		// Initialize apartment object with CRM data
		const apartment = {
			crm_unit_id: crmUnitId,
			id_original: getKeyValueFromInObjectPath(
				crmUnit,
				dictionary.unit_id_original_path,
				null
			),
			visibility: getCrmUnitVisibility(
				crmUnit,
				dictionary.visibility_config,
				VISIBLE
			),
		};

		//2. Unit Availability
		apartment.availability = getCrmUnitAvailability(
			crmUnit,
			dictionary.availability_data
		);

		//3. Price
		apartment.price_visible = getCrmUnitPriceVisibility(
			crmUnit,
			dictionary.price_config.price_visibility_config
		);
		apartment.price_brutto = getKeyValueFromInObjectPath(
			crmUnit,
			dictionary.price_config.price_brutto_path,
			null
		);
		apartment.price_m2 = getKeyValueFromInObjectPath(
			crmUnit,
			dictionary.price_config.price_m2_path,
			null
		);
		// apartment.price_history = getCrmUnitPriceHistory(
		// 	crmUnit,
		// 	dictionary.price_config.price_history_path
		// );
		// apartment.price_offer = getCrmUnitPriceOffer(
		// 	crmUnit,
		// 	dictionary.price_config.price_offer_path
		// );
		// apartment.price_offer_scheme = { on_offer: false, date: null };
		apartment.price_vat = getKeyValueFromInObjectPath(
			crmUnit,
			dictionary.price_config.price_vat_path,
			null
		);
		apartment.price_currency = getKeyValueFromInObjectPath(
			crmUnit,
			dictionary.price_config.price_currency_path,
			'PLN'
		);

		//4. Additional CRM apartment properties
		apartment.crm_project_id = getKeyValueFromInObjectPath(
			crmUnit,
			dictionary.project_id_path,
			null
		);
		apartment.crm_building_id = getKeyValueFromInObjectPath(
			crmUnit,
			dictionary.building_id_path,
			null
		);

		apartments.push({
			...apartment,
		});
	}

	return apartments;
};

export const buildApartmentsDataList = ({
	baseData,
	cmsData,
	crmData,
	crmDictionary,
	isExternalCRM,
	isInternalCRM,
	dictionaryError,
	crmError,
	cmsError,
	projectSetup,
}) => {
	const hideApartmentsOnError =
		projectSetup?.website?.crm_integration?.hide_apartments_on_error;

	if (
		(isExternalCRM && (dictionaryError || crmError)) ||
		(isInternalCRM && cmsError)
	) {
		const normalizedBase = normalizeBase(baseData);
		return normalizedBase.map((apartment) => ({
			...apartment,
			availability: UNKNOWN,
			price_brutto: UNKNOWN,
			price_m2: UNKNOWN,
			visibility: hideApartmentsOnError ? HIDDEN : VISIBLE,
		}));
	}

	const normalizedBase = normalizeBase(baseData);
	const normalizedCms = isInternalCRM && !cmsError ? normalizeCms(cmsData) : [];
	const normalizedCrm =
		isExternalCRM && !crmError && !dictionaryError
			? normalizeCrm(crmData, crmDictionary)
			: [];

	function mergeApartments(base, cms, crm) {
		const result = { ...base };
		[cms, crm].forEach((source) => {
			if (!source) return;
			Object.keys(source).forEach((key) => {
				if (source[key] !== null && source[key] !== undefined) {
					result[key] = source[key];
				}
			});
		});
		// Wyjątek: visibility - jeśli w którymkolwiek źródle jest HIDDEN, ustaw HIDDEN
		if (
			base?.visibility === HIDDEN ||
			(cms && cms.visibility === HIDDEN) ||
			(crm && crm.visibility === HIDDEN)
		) {
			result.visibility = HIDDEN;
		}

		return result;
	}

	const allProcessedApartments = normalizedBase.map((baseApartment) => {
		const cmsApartment = normalizedCms.find(
			(a) => a.id_original === baseApartment.id_original
		);
		const crmApartment = normalizedCrm.find(
			(a) => a.id_original === baseApartment.id_original
		);

		return mergeApartments(baseApartment, cmsApartment, crmApartment);
	});

	return allProcessedApartments.sort((a, b) =>
		a.id_cad.localeCompare(b.id_cad)
	);
};
